import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query';
import { employeesService } from '../services';
import type {
  EmployeeDetails,
  EmployeesQueryParams,
  EmploymentContractDetails,
} from '../types';
import { toast } from 'sonner';

// Query keys
export const employeesKeys = {
  all: ['employees'] as const,
  lists: () => [...employeesKeys.all, 'list'] as const,
  list: (folderUuid: string, params?: EmployeesQueryParams) =>
    [...employeesKeys.lists(), folderUuid, params] as const,
  details: () => [...employeesKeys.all, 'detail'] as const,
  detail: (folderUuid: string, employeeUuid: string) =>
    [...employeesKeys.details(), folderUuid, employeeUuid] as const,
  contractDetails: () => [...employeesKeys.all, 'contract-detail'] as const,
  contractDetail: (folderUuid: string, contractUuid: string) =>
    [...employeesKeys.contractDetails(), folderUuid, contractUuid] as const,
};

/**
 * Hook pour récupérer la liste des employés d'un dossier
 */
export function useEmployees(
  folderUuid: string | undefined,
  params?: EmployeesQueryParams
) {
  return useQuery({
    queryKey: employeesKeys.list(folderUuid!, params),
    queryFn: () => employeesService.getEmployees(folderUuid!, params),
    enabled: !!folderUuid,
  });
}

/**
 * Hook pour récupérer les détails d'un employé
 */
export function useEmployeeDetail(
  folderUuid: string | undefined,
  employeeUuid: string | undefined
) {
  return useQuery({
    queryKey:
      employeeUuid && folderUuid
        ? employeesKeys.detail(folderUuid, employeeUuid)
        : employeesKeys.detail('pending-folder', 'pending-employee'),
    queryFn: () =>
      employeesService.getEmployeeDetail(folderUuid!, employeeUuid!),
    enabled: !!folderUuid && !!employeeUuid,
  });
}

/**
 * Hook pour créer un employé
 */
export function useCreateEmployee(folderUuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeData: Partial<EmployeeDetails>) =>
      employeesService.createEmployee(folderUuid, employeeData),
    onSuccess: () => {
      // Invalider la liste des employés
      queryClient.invalidateQueries({
        queryKey: employeesKeys.lists(),
      });
      toast.success('Employé créé avec succès');
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la création: ${error.message}`);
    },
  });
}

/**
 * Hook pour mettre à jour un employé
 */
export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      employeeUuid,
      employeeData,
    }: {
      folderUuid: string;
      employeeUuid: string;
      employeeData: Partial<EmployeeDetails>;
    }) => employeesService.updateEmployee(employeeUuid, employeeData),
    onSuccess: (_, variables) => {
      // Invalider la liste et le détail
      queryClient.invalidateQueries({
        queryKey: employeesKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(
          variables.folderUuid,
          variables.employeeUuid
        ),
      });
      toast.success('Employé mis à jour avec succès');
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la mise à jour: ${error.message}`);
    },
  });
}

/**
 * Hook pour supprimer un employé
 */
export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeUuid: string) =>
      employeesService.deleteEmployee(employeeUuid),
    onSuccess: () => {
      // Invalider la liste
      queryClient.invalidateQueries({
        queryKey: employeesKeys.lists(),
      });
      toast.success('Employé supprimé avec succès');
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la suppression: ${error.message}`);
    },
  });
}

export function useEmployeeContractsDetails(
  folderUuid: string | undefined,
  contractUuids: string[]
) {
  const queryDefinitions =
    folderUuid && contractUuids.length > 0
      ? contractUuids.map(contractUuid => ({
          queryKey: employeesKeys.contractDetail(folderUuid, contractUuid),
          queryFn: () =>
            employeesService.getContractDetail(folderUuid, contractUuid),
          enabled: !!folderUuid && !!contractUuid,
        }))
      : [];

  const queries = useQueries({
    queries: queryDefinitions,
  });

  const data = queries
    .map(query => query.data)
    .filter(Boolean) as EmploymentContractDetails[];

  const isLoading = queries.some(query => query.isLoading);
  const isError = queries.some(query => query.isError);

  return {
    data,
    isLoading,
    isError,
    queries,
  };
}
